import { useState, useCallback, useEffect } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { handleFiles } from "../../../services/api/core";
import { getFolderInfo } from "../../../services/api/files";

/**
 * Hook for ticket attachment list, upload, reorder, and ordered filenames for PDF.
 * Used by RFI and T&M (and future ticket types) so attachment UX is implemented once.
 *
 * @param {Object} options
 * @param {string|number} options.ticketId - Ticket id for folder path API (e.g. RFI pk, report pk).
 * @param {string} [options.ticketIdentifier] - Display id (rfi_id, tm_id) for upload and cover label.
 * @param {function(options.ticketId): Promise<{folder_path: string}>} options.getAttachmentFolderPathApi
 * @param {string} options.uploadAction - handleFiles action (e.g. 'upload_rfi_attachment', 'upload_tm_attachment').
 * @param {Object} options.uploadAdditionalData - Data for handleFiles (e.g. { rfi_id }, { tm_id }).
 * @param {boolean} [options.disabled=false]
 * @param {string|null} [options.coverPageLabel] - If set, getOrderedAttachmentFilenames returns [coverPageLabel, ...files]. If null/undefined, returns only file names (for T&M).
 * @returns {Object} Attachment list state, upload/reorder handlers, and ordered filenames helper.
 */
export function useTicketAttachments({
  ticketId,
  ticketIdentifier,
  getAttachmentFolderPathApi,
  uploadAction,
  uploadAdditionalData,
  disabled = false,
  coverPageLabel = null,
}) {
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /**
   * List PDF/image files in the ticket attachment folder and assign `_id`s.
   *
   * @returns {Promise<void>}
   */
  const fetchAttachments = useCallback(async () => {
    if (ticketId == null) return;
    setIsLoadingAttachments(true);
    try {
      const pathResponse = await getAttachmentFolderPathApi(ticketId);
      const folderPath = pathResponse.folder_path;
      const folderInfo = await getFolderInfo(folderPath);
      const files = (folderInfo.files || []).filter((file) => {
        const name = (file.name || "").toLowerCase();
        return (
          name.endsWith(".pdf") || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)
        );
      });
      const filesWithIds = files.map((file, index) => ({
        ...file,
        _id: file.path || `attachment-${file.name}-${index}`,
      }));
      setAttachments(filesWithIds);
    } catch (err) {
      console.error("Failed to fetch attachments:", err);
      setAttachments([]);
    } finally {
      setIsLoadingAttachments(false);
    }
  }, [ticketId, getAttachmentFolderPathApi]);

  useEffect(() => {
    if (ticketId != null) {
      fetchAttachments();
    }
  }, [ticketId, fetchAttachments, refreshTrigger]);

  /**
   * Reorder attachments after a dnd-kit drag.
   *
   * @param {{active: {id: string|number}, over: {id: string|number}|null}} event Drag end event.
   * @returns {void}
   */
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setAttachments((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  /**
   * Filenames in current list order, optionally prefixed with `coverPageLabel`.
   *
   * @returns {string[]}
   */
  const getOrderedAttachmentFilenames = useCallback(() => {
    const names = attachments.map((file) => file.name);
    if (coverPageLabel != null && coverPageLabel !== "") {
      return [coverPageLabel, ...names];
    }
    return names;
  }, [attachments, coverPageLabel]);

  /**
   * Upload pending files. Optionally pass override additional_data (e.g. after
   * creating a ticket, pass { rfi_id: created.rfi_id } or { tm_id: created.tm_id }).
   * Do not pass the click event — use () => handleUploadAttachments() in onClick.
   * @param {Object} [overrideAdditionalData] - When provided, used instead of uploadAdditionalData (must be a plain object with keys like rfi_id/tm_id, not a React event)
   * @returns {Promise<void>}
   */
  const handleUploadAttachments = useCallback(
    async (overrideAdditionalData) => {
      // Ignore React synthetic events accidentally passed from onClick={handleUploadAttachments}
      const isEvent =
        overrideAdditionalData &&
        typeof overrideAdditionalData === "object" &&
        (overrideAdditionalData.nativeEvent !== undefined ||
          overrideAdditionalData.target !== undefined);
      const dataToUse = isEvent ? uploadAdditionalData : (overrideAdditionalData ?? uploadAdditionalData);
      if (pendingFiles.length === 0 || !dataToUse) return;
      setIsUploadingFiles(true);
      try {
        const filesForUpload = pendingFiles.map((file) => ({
          file,
          filename: file.name,
          type: file.type?.startsWith("image/") ? "image" : "pdf",
        }));
        await handleFiles({
          files: filesForUpload,
          action: uploadAction,
          additional_data: dataToUse,
        });
        setPendingFiles([]);
        setRefreshTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Failed to upload attachments:", err);
        throw err;
      } finally {
        setIsUploadingFiles(false);
      }
    },
    [pendingFiles, uploadAction, uploadAdditionalData]
  );

  return {
    attachments,
    setAttachments,
    pendingFiles,
    setPendingFiles,
    isLoadingAttachments,
    isUploadingFiles,
    fetchAttachments,
    handleDragEnd,
    getOrderedAttachmentFilenames,
    handleUploadAttachments,
    setRefreshTrigger,
  };
}
